import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Plus, Trash2, Loader2, ArrowRight, ShieldCheck, Mail, UserPlus, ArrowLeft } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import api, { formatApiError } from "@/lib/api";
import { TIERS, ADDON_PRICE, MAX_ITEMS } from "@/data/site";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function Checkout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const preselect = params.get("tier");
  const presetName = params.get("name") || "";
  const [items, setItems] = useState([{ tier: preselect && TIERS.some(t => t.id === preselect) ? preselect : "potency", supplement_name: presetName }]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  const isGuest = user === false;

  useEffect(() => {
    const c = params.get("cancelled");
    if (c) toast.info("Payment was cancelled. Your selection is saved.");
  }, [params]);

  const tierPrice = (id) => TIERS.find((t) => t.id === id)?.price || 0;
  // First test of each tier = full price; every additional test of the same tier = $50.
  const seenTiers = new Set();
  let subtotal = 0, charged = 0;
  items.forEach((it) => {
    const p = tierPrice(it.tier);
    const first = !seenTiers.has(it.tier);
    seenTiers.add(it.tier);
    subtotal += p;
    charged += first ? p : ADDON_PRICE;
  });
  const discount = Math.round((subtotal - charged) * 100) / 100;
  const total = Math.round(charged * 100) / 100;

  const updateItem = (i, key, val) => setItems(items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  const addItem = () => setItems((prev) => prev.length >= MAX_ITEMS ? prev : [...prev, { tier: prev[0]?.tier || "potency", supplement_name: "" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const fetchClientSecret = useCallback(async () => {
    const { data } = await api.post("/orders/checkout", {
      items: items.map((it) => ({ tier: it.tier, supplement_name: it.supplement_name })),
      email: isGuest ? email.trim() : undefined,
      origin_url: window.location.origin,
    });
    return data.client_secret;
  }, [items, email, isGuest]);

  const startPayment = async () => {
    if (isGuest && (!email.trim() || !email.includes("@"))) {
      toast.error("Please enter a valid email so we can deliver your report.");
      return;
    }
    setLoading(true);
    try {
      const secret = await fetchClientSecret();
      setClientSecret(secret);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  // --- Embedded payment view ---
  if (clientSecret) {
    return (
      <div className="bg-background" data-testid="checkout-page">
        <div className="mx-auto max-w-3xl px-6 py-12 md:px-10 md:py-16">
          <button onClick={() => setClientSecret(null)} data-testid="checkout-edit-order"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-ocean">
            <ArrowLeft className="h-4 w-4" /> Edit order
          </button>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-ocean">Complete your payment</h1>
          <p className="mt-2 text-slate-600">Enter your card details securely below — you won't leave this page. Total <span className="font-semibold text-ocean">${total.toFixed(2)}</span>.</p>
          <div className="mt-8 rounded-2xl border border-border bg-white p-2 sm:p-4" data-testid="checkout-embedded">
            <EmbeddedCheckoutProvider key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-teal" /> Payments are processed securely by Stripe. We never see your card number.</p>
        </div>
      </div>
    );
  }

  // --- Order builder view ---
  return (
    <div className="bg-background" data-testid="checkout-page">
      <div className="mx-auto max-w-5xl px-6 py-12 md:px-10 md:py-16">
        <h1 className="text-3xl font-bold tracking-tight text-ocean">Build your test order</h1>
        <p className="mt-2 text-slate-600">Add a test for each supplement you want checked. Each additional test of the same type is just <span className="font-semibold text-ocean">${ADDON_PRICE}</span> — up to {MAX_ITEMS} per order.</p>

        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
          <div className="space-y-4">
            {items.map((it, i) => (
              <div key={i} data-testid={`checkout-item-${i}`} className="rounded-2xl border border-border bg-white p-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-ocean">Test #{i + 1}</h3>
                  {items.length > 1 && (
                    <button onClick={() => removeItem(i)} data-testid={`checkout-remove-${i}`} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Test tier</label>
                    <select value={it.tier} onChange={(e) => updateItem(i, "tier", e.target.value)} data-testid={`checkout-tier-${i}`}
                      className="mt-1.5 w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-teal">
                      {TIERS.map((t) => <option key={t.id} value={t.id}>{t.name} — ${t.price}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Supplement name <span className="text-slate-400">(optional)</span></label>
                    <input value={it.supplement_name} onChange={(e) => updateItem(i, "supplement_name", e.target.value)} data-testid={`checkout-name-${i}`}
                      placeholder="e.g. Test Booster X" className="mt-1.5 w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-teal" />
                  </div>
                </div>
                <p className="mt-3 text-xs text-slate-500">{TIERS.find((t) => t.id === it.tier)?.included}</p>
              </div>
            ))}
            <button onClick={addItem} disabled={items.length >= MAX_ITEMS} data-testid="checkout-add-item" className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-teal/50 bg-accent/40 px-5 py-4 text-sm font-semibold text-teal-dark hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50">
              <Plus className="h-4 w-4" /> {items.length >= MAX_ITEMS ? `Maximum ${MAX_ITEMS} tests reached` : `Add another test — just $${ADDON_PRICE} each`}
            </button>

            {isGuest && (
              <div className="rounded-2xl border border-border bg-white p-6" data-testid="checkout-guest">
                <label className="flex items-center gap-2 text-sm font-semibold text-ocean"><Mail className="h-4 w-4 text-teal" /> Where should we send your report?</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="checkout-guest-email"
                  placeholder="you@email.com" className="mt-2 w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-teal" />
                <p className="mt-2 text-xs text-slate-500">No account needed — check out as a guest. After paying you can print your label and track results right away.</p>
                <button onClick={() => navigate("/login")} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-dark hover:text-ocean" data-testid="checkout-login-link">
                  <UserPlus className="h-3.5 w-3.5" /> Have an account? Log in
                </button>
              </div>
            )}
          </div>

          <div className="h-fit rounded-2xl border border-border bg-white p-6 lg:sticky lg:top-24" data-testid="checkout-summary">
            <h3 className="text-lg font-semibold text-ocean">Order summary</h3>
            <div className="mt-4 space-y-2.5 text-sm">
              <div className="flex justify-between text-slate-600"><span>Subtotal ({items.length} test{items.length > 1 ? "s" : ""})</span><span>${subtotal.toFixed(2)}</span></div>
              {discount > 0 && <div className="flex justify-between font-medium text-teal-dark"><span>Add-on savings ($50 each extra)</span><span>−${discount.toFixed(2)}</span></div>}
              <div className="mt-3 flex justify-between border-t border-border pt-3 text-base font-bold text-ocean"><span>Total</span><span data-testid="checkout-total">${total.toFixed(2)}</span></div>
            </div>
            <button onClick={startPayment} disabled={loading} data-testid="checkout-pay"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-ocean px-5 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-ocean-light disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue to payment <ArrowRight className="h-4 w-4" /></>}
            </button>
            <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-teal" /> Secure card payment — right on this page</p>
            <p className="mt-3 text-center text-xs text-slate-400">Money-back guarantee if results aren't delivered within 14 days.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
